<template>
  <div class="h-full flex bg-grey-navigation text-surface-user-blue">
    <div class="twilio-pip-dialer flex h-full flex-col w-full">
      <!-- Phone Number Header -->
      <div
        v-if="activeUserPhoneNumber && hasActiveCall"
        class="flex items-center justify-between border-b border-white/10 p-4"
      >
        <div>
          {{
            isOutgoing
              ? $t("DIALER.OUTGOING_CALL_FROM")
              : $t("DIALER.INCOMING_CALL_TO")
          }}:
        </div>
        <div class="flex items-center space-x-2">
          <div
            v-tooltip="$t('COMMON.COPY_TO_CLIPBOARD')"
            class="cursor-pointer"
            @click.stop.prevent="
              copyToClipboard(activeUserPhoneNumber, { isPiP: true })
            "
          >
            {{ activeUserPhoneNumber }}
          </div>
        </div>
      </div>

      <!-- Status Header (shown when no active call) -->
      <div
        v-else
        class="flex items-center justify-between border-b border-white/10 p-4"
      >
        <span class="font-semibold">{{ $t("DIALER.TITLE") }}</span>
        <div class="flex items-center space-x-2">
          <div
            v-if="deviceStatus"
            class="flex items-center space-x-2 text-xs"
            :class="statusClass"
          >
            <span class="size-2 rounded-full" :class="statusDotClass" />
            <span>{{ deviceStatus }}</span>
          </div>
        </div>
      </div>

      <!-- Call Info -->
      <div v-if="hasActiveCall" class="flex-1">
        <div
          class="flex w-full items-center justify-between border-b border-white/10 p-4"
        >
          <div class="flex items-center space-x-3 text-surface-user-blue">
            <lf-avatar
              :username="contactName || $t('COMMON.UNKNOWN')"
              :size="40"
            />
            <div class="flex min-w-0 flex-col">
              <span v-tooltipable.themeLight class="truncate font-semibold">
                {{ contactName || $t("COMMON.UNKNOWN") }}
              </span>
              <span data-cy="to-number" class="text-xs text-label">
                {{ contactNumber || "-" }}
              </span>
            </div>
          </div>
          <span
            v-if="terminalState"
            class="shrink-0 min-w-12 text-sm font-medium"
            :class="
              terminalState.status === 'busy' ? 'text-amber-400' : 'text-label'
            "
          >
            {{ $t(terminalState.config.statusText) }}
          </span>
          <span
            v-else-if="isEnding"
            class="shrink-0 min-w-12 text-sm font-medium text-label"
          >
            {{ $t("DIALER.ENDING") }}
          </span>
          <call-timer v-else-if="showCallTimer" class="shrink-0 min-w-12" />
        </div>
        <div class="call-details flex w-full flex-col space-y-1 p-4">
          <div
            v-for="row in infoRows"
            :key="row.name"
            :data-cy="row.name"
            class="call-session-info-row group"
          >
            <icon-base
              width="12"
              height="12"
              :view-box="row.iconOptions.viewBox"
              :icon="row.iconOptions.icon"
            />
            <span v-tooltipable.themeLight class="truncate">
              {{ row.value }}
            </span>
            <a
              v-if="row.link"
              :href="getRouteHref(row.link)"
              target="_opener"
              class="cursor-pointer"
            >
              <icon-base
                width="12"
                height="12"
                view-box="0 0 24 24"
                :icon="IconLink"
              />
            </a>
          </div>
        </div>
      </div>

      <!-- Idle State -->
      <div
        v-else
        class="flex flex-1 flex-col items-center justify-center p-4 text-center"
      >
        <icon-base
          :icon="IconDialerPhone"
          width="48"
          height="48"
          class="mb-4 text-label"
        />
        <p class="text-sm text-label">
          {{ $t("DIALER.WAITING_FOR_CALLS") }}
        </p>
        <p class="mt-2 text-xs text-label/60">
          {{ $t("DIALER.PIP_ACTIVE") }}
        </p>
      </div>

      <!-- Incoming Call Actions -->
      <div v-if="hasIncomingCall" class="twilio-dialer-actions">
        <div
          :title="$t('DIALER.IGNORE')"
          class="dialer-round-button flex-none bg-white/10"
          @click.stop.prevent="toggleSilence"
        >
          <icon-base
            :icon="isRingtoneSilenced ? IconSpeakerMute : IconSpeaker"
          />
        </div>
        <lf-button
          type="error"
          class="space-x-2 bg-error"
          centered
          :disabled="isProcessingCall"
          @click.stop.prevent="rejectCall"
        >
          <template #icon>
            <icon-base :icon="IconDialerPhone" class="rotate-135" />
          </template>
          <span>
            {{ isRejecting ? $t("COMMON.REJECTING") : $t("DIALER.DECLINE") }}
          </span>
        </lf-button>
        <lf-button
          type="success"
          class="space-x-2 bg-success"
          centered
          :disabled="isProcessingCall"
          @click.stop.prevent="acceptCall"
        >
          <template #icon>
            <icon-base :icon="IconDialerPhone" />
          </template>
          <span>
            {{ isAccepting ? $t("COMMON.CONNECTING") : $t("DIALER.ACCEPT") }}
          </span>
        </lf-button>
      </div>

      <!-- Terminal State Actions -->
      <div
        v-else-if="terminalState?.config?.showDismissButton"
        class="twilio-dialer-actions"
      >
        <div
          :title="$t('COMMON.DISCONNECT')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-error"
          @click.stop.prevent="dismissTerminalState"
        >
          <icon-base :icon="IconDialerPhone" class="rotate-135" />
        </div>
      </div>

      <!-- Ongoing Call Actions -->
      <div
        v-else-if="
          hasOngoingCall || deviceState === TwilioDeviceState.Initiating
        "
        class="twilio-dialer-actions"
      >
        <div
          :title="$t('DIALER.SHOW_KEYPAD')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white/10 hover:bg-label"
          @click.stop.prevent="toggleKeypad"
        >
          <icon-base :icon="IconCallKeypad" class="text-surface-user-blue" />
        </div>
        <div
          :title="isMicrophoneMuted ? $t('DIALER.UNMUTE') : $t('DIALER.MUTE')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white/10 hover:bg-label"
          @click.stop.prevent="toggleMute"
        >
          <icon-base
            :icon="isMicrophoneMuted ? IconMicrophoneMute : IconMicrophone"
          />
        </div>
        <div
          :title="$t('COMMON.DISCONNECT')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-error"
          @click.stop.prevent="disconnectCall"
        >
          <icon-base :icon="IconDialerPhone" class="rotate-135" />
        </div>
      </div>

      <!-- Device Selection -->
      <template v-if="device && isOutgoing">
        <twilio-device-select
          :devices="groupedDevices.input"
          :selected-device="device?.audio?.inputDevice?.deviceId"
          type="input"
          @device:select="setDeviceSettings"
          @device:refresh="authorizeUseMedia"
        />
        <twilio-device-select
          v-if="device?.audio?.isOutputSelectionSupported"
          :devices="groupedDevices.output"
          :selected-device="selectedOutputDevice"
          type="output"
          @device:select="setDeviceSettings"
          @device:refresh="authorizeUseMedia"
        />
      </template>
    </div>
    <call-keypad v-if="isKeypadVisible" @keypad:press="handleKeypadPress" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, toRaw, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteLocationRaw, useRouter } from "vue-router";

import * as Sentry from "@sentry/vue";
import noop from "lodash/noop";
import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { PhoneSelectionType } from "@/enums/communicationLogs";
import {
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioDeviceState,
} from "@/enums/communicationProviders";
import { TWILIO_PIP_ACCEPT_API_CALL } from "@/helpers/constants/events";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useNotification } from "@/hooks/notifications";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import { dialerApi } from "@/services/dialer-api";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";

import IconCallKeypad from "@/components/icons/IconCallKeypad.vue";
import IconDialerPhone from "@/components/icons/IconDialerPhone.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconMicrophone from "@/components/icons/IconMicrophone.vue";
import IconMicrophoneMute from "@/components/icons/IconMicrophoneMute.vue";
import IconPersons from "@/components/icons/IconPersons.vue";
import IconProfileSmall from "@/components/icons/IconProfileSmall.vue";
import IconSpeaker from "@/components/icons/IconSpeaker.vue";
import IconSpeakerMute from "@/components/icons/IconSpeakerMute.vue";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";
import CallKeypad from "@/components/twilio/CallKeypad.vue";
import CallTimer from "@/components/twilio/CallTimer.vue";
import TwilioDeviceSelect from "@/components/twilio/TwilioDeviceSelect.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfAvatar from "@/components/ui/LFAvatar.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";

import type { TerminalScreenConfig } from "@/services/dialer-api";
import type { Ref } from "vue";

const emit = defineEmits<{
  "keypad:toggle": [boolean];
}>();

const { t } = useI18n();
const router = useRouter();
const emitter = useEmitter();
const { trackEvent } = useAnalyticsStore();

const callStore = useCallsStore();
const {
  hasIncomingCall,
  hasOngoingCall,
  isMicrophoneMuted,
  isRingtoneSilenced,
  device,
  isCallActive,
  isDialing,
  deviceState,
  callDirection,
  activeCallSession,
  currentCall,
  activeCall,
  isKeypadVisible,
  showCallTimer,
} = storeToRefs(callStore);
const { savedPhoneNumber } = useTwilio();
const { showMessage } = useNotification();
const copyToClipboard = useClipboardWithMessage();

// Terminal/ending state from TwilioPiPManager
const terminalState = inject<
  Ref<{ status: string; config: TerminalScreenConfig } | null>
>("terminalState", ref(null));
const isEnding = inject<Ref<boolean>>("isEnding", ref(false));
const dismissTerminalState = inject<() => void>("dismissTerminalState", noop);

const audioDevices = ref<MediaDeviceInfo[] | null>(null);

// Loading states for Accept/Reject buttons
const isAccepting = ref(false);
const isRejecting = ref(false);
const isProcessingCall = computed(() => isAccepting.value || isRejecting.value);

const hasActiveCall = computed(
  () =>
    isCallActive.value ||
    isDialing.value ||
    !!terminalState.value ||
    isEnding.value
);

const groupedDevices = computed(() => {
  const empty: { input: MediaDeviceInfo[]; output: MediaDeviceInfo[] } = {
    input: [],
    output: [],
  };
  if (!audioDevices.value) {
    return empty;
  }
  return audioDevices.value.reduce((acc, curr) => {
    const type =
      curr.kind === "audioinput"
        ? "input"
        : curr.kind === "audiooutput"
          ? "output"
          : null;
    if (type) {
      acc[type].push(curr);
    }
    return acc;
  }, empty);
});

const selectedOutputDevice = computed(
  () =>
    [...(device.value?.audio?.speakerDevices?.get() ?? new Set())]?.[0]
      ?.deviceId ?? ""
);

const deviceStatus = computed(() => {
  if (terminalState.value) {
    return t(terminalState.value.config.statusText);
  }
  const stateMap: Partial<Record<TwilioDeviceState, string>> = {
    [TwilioDeviceState.Idle]: t("DIALER.READY"),
    [TwilioDeviceState.Incoming]: t("DIALER.INCOMING_CALL_TO"),
    [TwilioDeviceState.Ongoing]: t("DIALER.ONGOING_CALL"),
    [TwilioDeviceState.Initiating]: t("DIALER.RINGING"),
  };
  return stateMap[deviceState.value] ?? null;
});

const statusClass = computed(() => ({
  "text-success": deviceState.value === TwilioDeviceState.Idle && device.value,
  "text-warning": deviceState.value === TwilioDeviceState.Incoming,
  "text-primary": deviceState.value === TwilioDeviceState.Ongoing,
}));

const statusDotClass = computed(() => ({
  "bg-success": deviceState.value === TwilioDeviceState.Idle && device.value,
  "bg-warning animate-pulse": deviceState.value === TwilioDeviceState.Incoming,
  "bg-primary": deviceState.value === TwilioDeviceState.Ongoing,
}));

const isOutgoing = computed(
  () => callDirection.value === TwilioCallDirection.Outgoing
);

const contactName = computed(() => {
  if (isOutgoing.value) {
    return activeCallSession.value?.data?.callee?.name ?? null;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  // Fallback to API data when no Call object (ephemeral device pattern)
  return (
    rawCall?.customParameters?.get("callerFullName") ??
    callStore.apiIncomingCallData?.callerName ??
    null
  );
});

const contactNumber = computed(() => {
  if (isOutgoing.value) {
    return activeCallSession.value?.to ?? null;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  // Fallback to API data when no Call object (ephemeral device pattern)
  return (
    rawCall?.parameters?.From ??
    rawCall?.parameters?.To ??
    callStore.apiIncomingCallData?.from ??
    null
  );
});

const businessName = computed(() => {
  if (isOutgoing.value) {
    return (
      activeCallSession.value?.data?.call_sessionable?.business
        ?.business_legal_name ?? null
    );
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  // Fallback to API data when no Call object (ephemeral device pattern)
  return (
    rawCall?.customParameters?.get("callerBusinessName") ??
    callStore.apiIncomingCallData?.callerBusinessName ??
    null
  );
});

const partnerName = computed(() => {
  if (isOutgoing.value) {
    return activeCallSession.value?.partner ?? null;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  // Fallback to API data when no Call object (ephemeral device pattern)
  return (
    rawCall?.customParameters?.get("callerIntegrationPartner") ??
    callStore.apiIncomingCallData?.callerIntegrationPartner ??
    null
  );
});

const activeUserPhoneNumber = computed(() => {
  if (isOutgoing.value) {
    return (
      activeCallSession.value?.data?.from ??
      savedPhoneNumber.value?.phone_number ??
      PhoneSelectionType.Auto
    );
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  // For incoming: use calledNumber from API data (the actual phone number dialed)
  // Fallback to customParameters only if API data not available
  return (
    callStore.apiIncomingCallData?.calledNumber ??
    rawCall?.customParameters?.get("calledNumber") ??
    null
  );
});

const dealLink = computed(() => {
  if (isOutgoing.value) {
    return undefined;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  const applicationId =
    rawCall?.customParameters?.get("applicationId") ??
    callStore.apiIncomingCallData?.applicationId;
  if (!applicationId) {
    return undefined;
  }
  return {
    name: ROUTE_DEAL_PROGRESS,
    params: { id: applicationId },
  };
});

const infoRows = computed(() => {
  const sections = [
    {
      name: "callee-name",
      iconOptions: {
        icon: IconProfileSmall,
        viewBox: "0 0 16 16",
      },
      value: contactName.value,
    },
    {
      name: "business-name",
      iconOptions: {
        icon: IconDeals,
        viewBox: "10 10 20 20",
      },
      value: businessName.value,
      link: dealLink.value,
    },
    {
      name: "partner-name",
      iconOptions: {
        icon: IconPersons,
        viewBox: "10 10 20 20",
      },
      value: partnerName.value,
    },
  ];
  return sections.filter((section) => !!section.value);
});

const getRouteHref = (route: RouteLocationRaw): string => {
  return router.resolve(route).href;
};

const handleKeypadPress = (key: string) => {
  activeCall.value?.sendDigits(key);
  trackEvent(AnalyticsEvent.TwilioDtmfSent);
};

const acceptCall = () => {
  if (isProcessingCall.value) return;

  isAccepting.value = true;
  emitter.emit(TWILIO_PIP_ACCEPT_API_CALL);
  trackEvent(AnalyticsEvent.TwilioCallAccepted);
  // Note: isAccepting stays true until call connects or fails.
  // The handleAcceptApiCall in TwilioPiPManager handles the actual flow.
  // Watcher on deviceState will clear the flag on success or idle.
};

const toggleKeypad = () => {
  isKeypadVisible.value = !isKeypadVisible.value;
  emit("keypad:toggle", isKeypadVisible.value);
  trackEvent(AnalyticsEvent.TwilioKeypadToggled, {
    action: isKeypadVisible.value ? "opened" : "closed",
  });
};

const rejectCall = async () => {
  if (isProcessingCall.value) return;

  isRejecting.value = true;

  // Immediately notify ALL other tabs to stop ringing — before the async API call
  callStore.stopIncomingSound();
  callStore.notifyIncomingCallHandled();

  try {
    await dialerApi.rejectCall();
    callStore.clearCallState();
    callStore.notifyCallEnded();
    trackEvent(AnalyticsEvent.TwilioCallRejected);
  } catch (error) {
    console.error("[TwilioPiPDialer] Failed to reject call via API:", error);
    isRejecting.value = false; // Re-enable on error so user can retry
  }
};

const disconnectCall = () => {
  const direction = callDirection.value;
  if (activeCall.value) {
    toRaw(activeCall.value).disconnect();
  }
  if (device.value && toRaw(device.value).isBusy) {
    toRaw(device.value).calls?.forEach((c) => c.disconnect());
  }
  trackEvent(AnalyticsEvent.TwilioCallDisconnected, { direction });
  // SDK disconnect event → handleCallEnded → startCallEnding handles cleanup
};

const toggleMute = () => {
  if (activeCall.value) {
    const newMuteState = !isMicrophoneMuted.value;
    toRaw(activeCall.value).mute(newMuteState);
    trackEvent(AnalyticsEvent.TwilioMicrophoneToggled, {
      action: newMuteState ? "muted" : "unmuted",
    });
  }
};

const toggleSilence = () => {
  if (isRingtoneSilenced.value) {
    callStore.playIncomingSound();
    isRingtoneSilenced.value = false;
    trackEvent(AnalyticsEvent.TwilioRingtoneToggled, { action: "unmuted" });
  } else {
    callStore.stopIncomingSound();
    isRingtoneSilenced.value = true;
    callStore.notifyCallSilenced();
    trackEvent(AnalyticsEvent.TwilioRingtoneToggled, { action: "silenced" });
  }
};

const authorizeUseMedia = async () => {
  try {
    const permissions = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    if (permissions?.state !== "granted") {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => (audioDevices.value = devices));
  } catch (err) {
    showMessage(t("TWILIO.DEVICE_AUTHORIZATION_ERROR"), "error");
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.DeviceAuthorizationError,
      explanation: t("TWILIO.DEVICE_AUTHORIZATION_ERROR"),
    });
    Sentry.captureException(err);
  }
};

const setDeviceSettings = (
  selectedDevice: string,
  type: "input" | "output"
) => {
  if (!device.value) {
    return;
  }
  if (type === "input") {
    toRaw(device.value).audio?.setInputDevice(selectedDevice);
  } else {
    toRaw(device.value).audio?.speakerDevices?.set(selectedDevice);
  }
  trackEvent(AnalyticsEvent.TwilioAudioDeviceSelected, { type });
};

// Clear loading states when call state changes (connected, ended, etc.)
watch(
  () => callStore.deviceState,
  (newState) => {
    if (newState === TwilioDeviceState.Ongoing) {
      // Call connected — clear accepting state
      isAccepting.value = false;
    }
    if (newState === TwilioDeviceState.Idle) {
      // Call ended/rejected — clear all loading states
      isAccepting.value = false;
      isRejecting.value = false;
    }
  }
);

onMounted(async () => {
  await authorizeUseMedia();
});
// Note: Do NOT clear call state on unmount - the call should continue
// even if PiP is closed. Call state is managed by TwilioPiPManager.
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.call-session-info-row {
  @apply flex h-[23px] items-center justify-start space-x-2;

  .icon {
    @apply flex-none text-label;
  }
}

.dialer-round-button {
  @apply flex size-10 cursor-pointer items-center justify-center rounded-full;
}

.twilio-dialer-actions {
  @apply mt-auto flex w-full items-center justify-center space-x-3 p-4;
}

.twilio-pip-dialer {
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
  min-height: 100%;
}
</style>
